// SMS Marketing Settings Tab
import React from "react";
import SettingsSaveButtons from "./SettingsSaveButtons";

const SMSMarketingSettings = ({
  settings,
  updateSettings,
  saveSettings,
  saving,
  hasUnsavedChanges,
  resetSettings,
}) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    saveSettings();
  };

  // Helper to update nested smsMarketing fields safely
  const updateSmsField = (field, value) => {
    updateSettings({
      smsMarketing: {
        ...(settings.smsMarketing || {}),
        [field]: value,
      },
    });
  };

  // Helper to update nested pricing fields
  const updatePricingField = (field, value) => {
    updateSettings({
      smsMarketing: {
        ...(settings.smsMarketing || {}),
        pricing: {
          ...(settings.smsMarketing?.pricing || {}),
          [field]: value,
        },
      },
    });
  };

  // Handle base price change and recalculate bulk discounts
  const handleBasePriceChange = (newBasePrice) => {
    const updatedDiscounts =
      settings.smsMarketing?.pricing?.bulkDiscounts?.map((discount) => ({
        ...discount,
        pricePerCredit: parseFloat(
          (newBasePrice * (1 - discount.discountPercent / 100)).toFixed(4),
        ),
      })) || [];

    updateSettings({
      smsMarketing: {
        ...(settings.smsMarketing || {}),
        pricing: {
          ...(settings.smsMarketing?.pricing || {}),
          basePrice: newBasePrice,
          bulkDiscounts: updatedDiscounts,
        },
      },
    });
  };

  // Update a specific bulk discount
  const updateBulkDiscount = (index, field, value) => {
    const newDiscounts = [
      ...(settings.smsMarketing?.pricing?.bulkDiscounts || []),
    ];
    newDiscounts[index] = { ...newDiscounts[index], [field]: value };

    // If discount percent changes, recalculate price
    if (field === "discountPercent") {
      const basePrice = settings.smsMarketing?.pricing?.basePrice || 0.05;
      newDiscounts[index].pricePerCredit = parseFloat(
        (basePrice * (1 - value / 100)).toFixed(4),
      );
    }

    updateSettings({
      smsMarketing: {
        ...(settings.smsMarketing || {}),
        pricing: {
          ...(settings.smsMarketing?.pricing || {}),
          bulkDiscounts: newDiscounts,
        },
      },
    });
  };

  // Add new bulk discount tier
  const addBulkDiscount = () => {
    const basePrice = settings.smsMarketing?.pricing?.basePrice || 0.05;
    const defaultDiscountPercent = 20;
    const calculatedPrice = parseFloat(
      (basePrice * (1 - defaultDiscountPercent / 100)).toFixed(4),
    );

    const newDiscounts = [
      ...(settings.smsMarketing?.pricing?.bulkDiscounts || []),
      {
        minCredits: 1000,
        pricePerCredit: calculatedPrice,
        discountPercent: defaultDiscountPercent,
      },
    ];

    updatePricingField("bulkDiscounts", newDiscounts);
  };

  // Remove bulk discount tier
  const removeBulkDiscount = (index) => {
    const newDiscounts = (
      settings.smsMarketing?.pricing?.bulkDiscounts || []
    ).filter((_, i) => i !== index);
    updatePricingField("bulkDiscounts", newDiscounts);
  };

  const basePrice = settings.smsMarketing?.pricing?.basePrice || 0.05;
  const bulkDiscounts = settings.smsMarketing?.pricing?.bulkDiscounts || [];

  return (
    <form onSubmit={handleSubmit}>
      {/* SMS Pricing & Credits Section */}
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          padding: "32px",
          marginBottom: "24px",
        }}
      >
        <h2
          style={{ fontSize: "20px", fontWeight: "600", marginBottom: "8px" }}
        >
          📱 SMS Pricing & Credits
        </h2>
        <p style={{ color: "#6b7280", marginBottom: "24px", fontSize: "14px" }}>
          Configure SMS credit pricing, free monthly allowances, bulk discounts,
          and warning thresholds.
        </p>

        {/* Basic Settings */}
        <div style={{ marginBottom: "28px" }}>
          <h3
            style={{
              fontSize: "16px",
              fontWeight: "600",
              marginBottom: "16px",
              color: "#374151",
            }}
          >
            Basic Settings
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "24px",
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "8px",
                }}
              >
                Monthly Free Credits Per Shop
              </label>
              <input
                type="number"
                min="0"
                max="500"
                value={settings.smsMarketing?.monthlyCreditsPerShop ?? ""}
                onChange={(e) =>
                  updateSmsField(
                    "monthlyCreditsPerShop",
                    parseInt(e.target.value),
                  )
                }
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              />
              <p
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  margin: "4px 0 0",
                }}
              >
                Free SMS each shop location gets monthly
              </p>
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "8px",
                }}
              >
                Monthly Free Credits Per Freelancer
              </label>
              <input
                type="number"
                min="0"
                max="500"
                value={settings.smsMarketing?.monthlyCreditsPerFreelancer ?? ""}
                onChange={(e) =>
                  updateSmsField(
                    "monthlyCreditsPerFreelancer",
                    parseInt(e.target.value),
                  )
                }
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              />
              <p
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  margin: "4px 0 0",
                }}
              >
                Free SMS each freelancer gets monthly
              </p>
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "8px",
                }}
              >
                Low Credit Threshold
              </label>
              <input
                type="number"
                min="0"
                max="50"
                value={settings.smsMarketing?.lowCreditThreshold ?? ""}
                onChange={(e) =>
                  updateSmsField("lowCreditThreshold", parseInt(e.target.value))
                }
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              />
              <p
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  margin: "4px 0 0",
                }}
              >
                Warn when shop credits below this
              </p>
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "8px",
                }}
              >
                Urgent Threshold
              </label>
              <input
                type="number"
                min="0"
                max="20"
                value={settings.smsMarketing?.urgentCreditThreshold ?? ""}
                onChange={(e) =>
                  updateSmsField(
                    "urgentCreditThreshold",
                    parseInt(e.target.value),
                  )
                }
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              />
              <p
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  margin: "4px 0 0",
                }}
              >
                Critical low credit threshold
              </p>
            </div>
          </div>
        </div>

        {/* Pricing Settings */}
        <div style={{ marginBottom: "28px" }}>
          <h3
            style={{
              fontSize: "16px",
              fontWeight: "600",
              marginBottom: "16px",
              color: "#374151",
            }}
          >
            Pricing
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "24px",
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "8px",
                }}
              >
                Base Price Per SMS ($)
              </label>
              <input
                type="number"
                step="0.001"
                min="0"
                max="1"
                value={settings.smsMarketing?.pricing?.basePrice ?? ""}
                onChange={(e) =>
                  handleBasePriceChange(parseFloat(e.target.value))
                }
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              />
              <p
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  margin: "4px 0 0",
                }}
              >
                Standard price per credit
              </p>
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "8px",
                }}
              >
                Minimum Purchase (Credits)
              </label>
              <input
                type="number"
                min="10"
                max="1000"
                value={settings.smsMarketing?.pricing?.minimumPurchase ?? ""}
                onChange={(e) =>
                  updatePricingField(
                    "minimumPurchase",
                    parseInt(e.target.value),
                  )
                }
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              />
              <p
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  margin: "4px 0 0",
                }}
              >
                Minimum credits per purchase
              </p>
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "8px",
                }}
              >
                Currency
              </label>
              <select
                value={settings.smsMarketing?.pricing?.currency ?? "USD"}
                onChange={(e) => updatePricingField("currency", e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="CAD">CAD</option>
              </select>
              <p
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  margin: "4px 0 0",
                }}
              >
                Pricing currency
              </p>
            </div>
          </div>
        </div>

        {/* Bulk Discounts */}
        <div style={{ marginBottom: "28px" }}>
          <h3
            style={{
              fontSize: "16px",
              fontWeight: "600",
              marginBottom: "16px",
              color: "#374151",
            }}
          >
            Bulk Discount Tiers
          </h3>
          {bulkDiscounts.map((discount, index) => (
            <div
              key={index}
              style={{
                background: "#f9fafb",
                padding: "12px",
                borderRadius: "6px",
                marginBottom: "10px",
                border: "1px solid #e5e7eb",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: "32px",
                  alignItems: "flex-end",
                }}
              >
                <div style={{ width: "150px" }}>
                  <label
                    style={{
                      display: "block",
                      fontWeight: "500",
                      marginBottom: "4px",
                      fontSize: "12px",
                    }}
                  >
                    Minimum Credits
                  </label>
                  <input
                    type="number"
                    min="100"
                    value={discount.minCredits}
                    onChange={(e) =>
                      updateBulkDiscount(
                        index,
                        "minCredits",
                        parseInt(e.target.value),
                      )
                    }
                    style={{
                      width: "100%",
                      padding: "6px 8px",
                      border: "1px solid #d1d5db",
                      borderRadius: "4px",
                      fontSize: "13px",
                    }}
                  />
                  <div style={{ height: "14px" }}></div>
                </div>

                <div style={{ width: "150px" }}>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontWeight: "500",
                        marginBottom: "4px",
                        fontSize: "12px",
                      }}
                    >
                      Price Per Credit ($)
                    </label>
                    <input
                      type="text"
                      value={discount.pricePerCredit.toFixed(4)}
                      disabled
                      style={{
                        width: "100%",
                        padding: "6px 8px",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        fontSize: "13px",
                        backgroundColor: "#f3f4f6",
                        color: "#6b7280",
                        cursor: "not-allowed",
                      }}
                    />
                  </div>
                  <p
                    style={{
                      fontSize: "10px",
                      color: "#9ca3af",
                      margin: "2px 0 0",
                      fontStyle: "italic",
                      height: "14px",
                    }}
                  >
                    Auto-calculated
                  </p>
                </div>

                <div style={{ width: "150px" }}>
                  <label
                    style={{
                      display: "block",
                      fontWeight: "500",
                      marginBottom: "4px",
                      fontSize: "12px",
                    }}
                  >
                    Discount %
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="50"
                    value={discount.discountPercent}
                    onChange={(e) =>
                      updateBulkDiscount(
                        index,
                        "discountPercent",
                        parseInt(e.target.value) || 0,
                      )
                    }
                    style={{
                      width: "100%",
                      padding: "6px 8px",
                      border: "1px solid #d1d5db",
                      borderRadius: "4px",
                      fontSize: "13px",
                    }}
                  />
                  <div style={{ height: "14px" }}></div>
                </div>

                <div style={{ width: "150px" }}>
                  <div style={{ height: "18px" }}></div>
                  <button
                    type="button"
                    onClick={() => removeBulkDiscount(index)}
                    style={{
                      width: "100%",
                      padding: "6px",
                      background: "#ef4444",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "12px",
                      fontWeight: "500",
                      height: "32px",
                    }}
                  >
                    Remove
                  </button>
                  <div style={{ height: "14px" }}></div>
                </div>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={addBulkDiscount}
            style={{
              padding: "8px 16px",
              background: "#10b981",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: "500",
            }}
          >
            + Add Discount Tier
          </button>
        </div>

        {/* Preview */}
        <div
          style={{
            background: "#eff6ff",
            border: "1px solid #3b82f6",
            borderRadius: "8px",
            padding: "16px",
          }}
        >
          <h4
            style={{
              fontSize: "14px",
              fontWeight: "600",
              marginBottom: "12px",
              color: "#1e40af",
            }}
          >
            💡 Pricing Preview
          </h4>
          <div style={{ fontSize: "13px", color: "#1e3a8a" }}>
            <div style={{ marginBottom: "8px" }}>
              <strong>100 credits:</strong> $
              {basePrice ? (100 * basePrice).toFixed(2) : "0.00"}
            </div>
            {bulkDiscounts[0] && (
              <div style={{ marginBottom: "8px" }}>
                <strong>1,000 credits:</strong> $
                {(1000 * bulkDiscounts[0].pricePerCredit).toFixed(2)}{" "}
                <span style={{ color: "#10b981", fontWeight: "600" }}>
                  ({bulkDiscounts[0].discountPercent}% off)
                </span>
              </div>
            )}
            {bulkDiscounts[1] && (
              <div>
                <strong>5,000 credits:</strong> $
                {(5000 * bulkDiscounts[1].pricePerCredit).toFixed(2)}{" "}
                <span style={{ color: "#10b981", fontWeight: "600" }}>
                  ({bulkDiscounts[1].discountPercent}% off)
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Save Buttons */}
      <SettingsSaveButtons
        onSave={handleSubmit}
        onCancel={resetSettings}
        saving={saving}
        hasChanges={hasUnsavedChanges}
      />

      {/* Info Box */}
      <div
        style={{
          marginTop: "24px",
          padding: "16px",
          background: "#eff6ff",
          border: "1px solid #3b82f6",
          borderRadius: "8px",
          fontSize: "13px",
          color: "#1e40af",
        }}
      >
        <strong>ℹ️ Important Notes:</strong>
        <ul style={{ margin: "8px 0 0", paddingLeft: "20px" }}>
          <li>Monthly free credits reset on the 1st of each month</li>
          <li>
            Bulk discount tiers apply automatically based on purchase quantity
          </li>
          <li>Thresholds trigger warning notifications to shop owners</li>
          <li>
            Credits are shared across all locations within a business (for
            multi-location accounts)
          </li>
        </ul>
      </div>
    </form>
  );
};

export default SMSMarketingSettings;
